<template>
  <section class="dashboard-container" v-loading="loading">
    <!-- Hero 欢迎横幅 -->
    <div class="dashboard-hero">
      <div class="hero-left">
        <div class="hero-greeting">
          <h1>欢迎回来，{{ username }}</h1>
          <p>游戏发行中台，当前正在管理：<strong class="hero-app-name">{{ appName }}</strong></p>
        </div>
        <div class="hero-stats">
          <div class="hero-stat-item">
            <span class="hero-stat-label">系统运行状态</span>
            <span class="hero-stat-value status-ok">
              <span class="status-dot animate-pulse"></span>正常运行中
            </span>
          </div>
          <div class="hero-stat-item">
            <span class="hero-stat-label">当前时间</span>
            <span class="hero-stat-value text-time">{{ currentTime }}</span>
          </div>
        </div>
      </div>
      <div class="hero-right">
        <button class="btn-refresh" type="button" @click="fetchData">
          <i class="el-icon-refresh"></i> 刷新数据
        </button>
      </div>
      <!-- 3D 浮动背景插图 -->
      <img src="@/assets/img/dashboard-hero-bg.png" class="hero-illustration" alt="Illustration" />
    </div>

    <!-- 快捷操作网格 -->
    <div class="quick-grid">
      <button
        v-for="item in quickActions"
        :key="item.path"
        class="quick-card"
        type="button"
        @click="go(item.path, item.query)"
      >
        <div class="quick-card-left">
          <span class="quick-icon" :class="item.tone">
            <font-awesome-icon :icon="item.icon" />
          </span>
          <div class="quick-info">
            <span class="quick-title">{{ item.title }}</span>
            <span class="quick-desc">{{ item.desc }}</span>
          </div>
        </div>
        <i class="el-icon-arrow-right quick-arrow"></i>
      </button>
    </div>

    <!-- 底部双栏布局 -->
    <div class="dashboard-layout">
      <!-- 左列：最近使用记录 -->
      <div class="main-column">
        <div class="panel">
          <div class="panel-header">
            <div>
              <h2>最近使用记录</h2>
              <p>记录您在工作台中最近访问过的常用功能（最多展示8条）</p>
            </div>
            <button class="btn-more" type="button" @click="go('/c/cl')">
              查看全部渠道
            </button>
          </div>
          <el-table :data="recentRecords" size="medium" class="dashboard-table" empty-text="暂无可展示记录">
            <el-table-column prop="type" label="类型" width="100">
              <template slot-scope="scope">
                <span class="badge-type" :class="getBadgeClass(scope.row.type)">
                  {{ scope.row.type }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="name" label="名称" min-width="120"></el-table-column>
            <el-table-column prop="meta" label="关联信息" min-width="180">
              <template slot-scope="scope">
                <span class="text-meta">{{ scope.row.meta }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="timeText" label="时间" width="180"></el-table-column>
            <el-table-column label="操作" width="90" align="center">
              <template slot-scope="scope">
                <el-button type="text" size="small" class="btn-table-action" @click="go(scope.row.path, scope.row.query)">打开</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <!-- 右列：中台核心业务接入流程引导 (基于 docs/功能文档.md，离线零 API 依赖) -->
      <div class="side-column">
        <!-- 业务接入主链路 -->
        <div class="panel workflow-panel" style="height: 100%;">
          <div class="panel-header">
            <div>
              <h2><i class="el-icon-guide" style="margin-right: 6px; color: #2563eb;"></i> 游戏中台接入全景流程</h2>
              <p>官方渠道与商户接入生命周期时序引导</p>
            </div>
          </div>
          <div class="workflow-steps">
            <div class="workflow-step-item">
              <div class="step-num">1</div>
              <div class="step-content">
                <span class="step-title" @click="go('/company')">建主体与元游戏</span>
                <p class="step-desc">在公司管理建公司，在产品管理新建游戏（绑定网关域名与 UFO 分组）。</p>
              </div>
            </div>

            <div class="workflow-step-item">
              <div class="step-num">2</div>
              <div class="step-content">
                <span class="step-title" @click="go('/pl')">规划渠道号区间</span>
                <p class="step-desc">在平台规划为当前游戏划分独占号段 [min, max) 约束渠道范围。</p>
              </div>
            </div>

            <div class="workflow-step-item">
              <div class="step-num">3</div>
              <div class="step-content">
                <span class="step-title" @click="go('/l')">准备逻辑与类型字典</span>
                <p class="step-desc">在逻辑管理建运行参数 Logic 模板，在渠道类型库录入物理包字典。</p>
              </div>
            </div>

            <div class="workflow-step-item">
              <div class="step-num">4</div>
              <div class="step-content">
                <span class="step-title" @click="go('/c/cl')">核心渠道接入</span>
                <p class="step-desc">在渠道列表创建新渠道包（ID自动联动），配置逻辑参数、默认档位与合规开关。</p>
              </div>
            </div>

            <div class="workflow-step-item">
              <div class="step-num">5</div>
              <div class="step-content">
                <span class="step-title" @click="go('/xs/args/game')">官方 XS 渠道配置</span>
                <p class="step-desc">（若使用官方SDK）于 XS游戏/XS渠道 配置登录支付 Args，并在电信认证配置实名凭据。</p>
              </div>
            </div>

            <div class="workflow-step-item">
              <div class="step-num">6</div>
              <div class="step-content">
                <span class="step-title" @click="go('/mch/group')">支付商户号及商户组</span>
                <p class="step-desc">配置微信/支付宝商户凭据，组装商户组并为渠道配置随机或默认支付商户。</p>
              </div>
            </div>

            <div class="workflow-step-item">
              <div class="step-num">7</div>
              <div class="step-content">
                <span class="step-title" @click="go('/n')">主机节点同步与上线</span>
                <p class="step-desc">去同步参数页将配置下发至业务节点，点击顶栏重载刷新 Nacos 缓存生效上线。</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'index',
  data () {
    return {
      loading: false,
      currentTime: '',
      recentRecords: [],
      timer: null
    }
  },
  computed: {
    appId () {
      return this.$store.getters.appId
    },
    appName () {
      const info = this.$store.getters.appInfo || {}
      return info.name || '未选择游戏'
    },
    username () {
      return this.$store.getters.name || 'Admin'
    },
    todayChanges () {
      return 8
    },
    alarmCount () {
      return 2
    },
    quickActions () {
      return [
        { title: '游戏管理', desc: '管理游戏及产品线配置', path: '/p', icon: 'gamepad', tone: 'blue', typeText: '产品' },
        { title: '渠道接入', desc: '配置并接入游戏渠道', path: '/c/cl', icon: 'plug', tone: 'purple', typeText: '渠道' },
        { title: '商户配置', desc: '配置商户组及收款账户', path: '/mch/group', icon: 'wallet', tone: 'amber', typeText: '商户' },
        { title: '电信认证', desc: '管理电信短信与认证参数', path: '/xs/telecom/auth', icon: 'certificate', tone: 'indigo', typeText: '认证' },
        { title: '登录支付', desc: '配置游戏登录与支付参数', path: '/xs/args/game', icon: 'coins', tone: 'green', typeText: '参数' },
        { title: '官包配置', desc: '配置XS官方渠道参数', path: '/xs/args/channel', icon: 'shield-alt', tone: 'cyan', typeText: '渠道' },
        { title: '扩展游戏', desc: '配置游戏扩展运行参数', path: '/ext/config/game', icon: 'puzzle-piece', tone: 'rose', typeText: '扩展' },
        { title: '扩展渠道', desc: '配置渠道的扩展特性参数', path: '/ext/config/channel', icon: 'link', tone: 'teal', typeText: '扩展' }
      ]
    }
  },
  watch: {
    appId (val, oldVal) {
      if (val !== oldVal) this.fetchData()
    }
  },
  created () {
    this.updateTime()
    this.timer = setInterval(this.updateTime, 60000)
    this.loadRecords()
  },
  beforeDestroy () {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    fetchData () {
      this.loading = true
      this.updateTime()
      this.loadRecords()
      setTimeout(() => {
        this.loading = false
      }, 300)
    },
    loadRecords () {
      const cacheKey = `recent_records_${this.username}`
      const cached = localStorage.getItem(cacheKey)
      if (cached) {
        try {
          this.recentRecords = JSON.parse(cached)
        } catch (e) {
          this.recentRecords = this.getDefaultRecords()
        }
      } else {
        this.recentRecords = this.getDefaultRecords()
      }
    },
    getDefaultRecords () {
      return [
        { type: '产品', name: '游戏管理', meta: '关联路径: /p', timeText: '今天 10:24', path: '/p', query: {} },
        { type: '渠道', name: '渠道接入', meta: '关联路径: /c/cl', timeText: '今天 09:15', path: '/c/cl', query: {} },
        { type: '商户', name: '商户配置', meta: '关联路径: /mch/group', timeText: '昨天 17:40', path: '/mch/group', query: {} },
        { type: '参数', name: '登录支付', meta: '关联路径: /xs/args/game', timeText: '昨天 15:30', path: '/xs/args/game', query: {} },
        { type: '认证', name: '电信认证', meta: '关联路径: /xs/telecom/auth', timeText: '昨天 11:20', path: '/xs/telecom/auth', query: {} },
        { type: '渠道', name: '官包配置', meta: '关联路径: /xs/args/channel', timeText: '前天 14:10', path: '/xs/args/channel', query: {} },
        { type: '扩展', name: '扩展游戏', meta: '关联路径: /ext/config/game', timeText: '前天 10:05', path: '/ext/config/game', query: {} },
        { type: '扩展', name: '扩展渠道', meta: '关联路径: /ext/config/channel', timeText: '前天 09:30', path: '/ext/config/channel', query: {} }
      ]
    },
    updateTime () {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const date = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      this.currentTime = `${year}-${month}-${date} ${hours}:${minutes}`
    },
    getBadgeClass (type) {
      const map = {
        产品: 'channel',
        渠道: 'channel',
        广告: 'platform',
        运维: 'args',
        公司: 'platform',
        商户: 'args',
        认证: 'platform',
        参数: 'args',
        扩展: 'platform',
        其它: 'channel'
      }
      return map[type] || 'channel'
    },
    go (path, query) {
      this.$router.push({ path, query: query || {} })
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  padding: 16px 24px 32px;
  color: var(--text-main);
  background-color: var(--bg-page);
  min-height: calc(100vh - 56px);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  box-sizing: border-box;
}

/* Hero 欢迎横幅 */
.dashboard-hero {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  padding: 28px 36px;
  background: var(--bg-hero);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(37, 99, 235, 0.03), 0 2px 4px rgba(37, 99, 235, 0.01);
  position: relative;
  overflow: hidden;
  margin-bottom: 20px;
}

.hero-left {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 2;
  flex: 1;
}

.hero-greeting h1 {
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  color: var(--text-main);
  letter-spacing: -0.5px;
}

.hero-greeting p {
  margin: 8px 0 0 0;
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
}

.hero-app-name {
  color: var(--primary-color);
  font-weight: 700;
}

.hero-stats {
  display: flex;
  gap: 40px;
  margin-top: 32px;
}

.hero-stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.hero-stat-label {
  font-size: 12px;
  color: var(--text-muted);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.hero-stat-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-main);
  display: flex;
  align-items: center;
  gap: 6px;
}

.hero-stat-value.status-ok {
  color: #10b981;
}

.text-time {
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
}

.status-dot {
  width: 8px;
  height: 8px;
  background-color: #10b981;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.6);
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: .5;
    transform: scale(1.1);
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.hero-right {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-end;
  position: relative;
  width: 320px;
  flex-shrink: 0;
  z-index: 2;
}

.btn-refresh {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
  z-index: 10;
}

.btn-refresh:hover {
  background: var(--bg-hover);
  color: var(--text-main);
  border-color: var(--border-hover);
}

.hero-illustration {
  position: absolute;
  right: 120px;
  top: 0;
  height: 100%;
  width: auto;
  max-width: none !important;
  max-height: none !important;
  pointer-events: none;
  z-index: 1;
  opacity: 0.95;
  transition: opacity 0.2s ease;
}
html.dark .hero-illustration {
  opacity: 0.75;
}

/* 快捷操作网格 */
.quick-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.quick-card {
  border: 1px solid var(--border-color);
  background: var(--bg-card);
  border-radius: 14px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.01);
}

.quick-card:hover {
  transform: translateY(-2px);
  border-color: var(--border-hover);
  box-shadow: var(--shadow-base);
}
html.dark .quick-card:hover {
  border-color: rgba(37, 99, 235, 0.35);
}

.quick-card-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.quick-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  transition: background-color 0.2s, color 0.2s;
}

.quick-icon.blue { background: #eff6ff; color: #2563eb; }
.quick-icon.green { background: #f0fdf4; color: #16a34a; }
.quick-icon.amber { background: #fffbeb; color: #d97706; }
.quick-icon.purple { background: #faf5ff; color: #7c3aed; }
.quick-icon.indigo { background: #eef2ff; color: #4f46e5; }
.quick-icon.cyan { background: #ecfeff; color: #0891b2; }
.quick-icon.rose { background: #fff1f2; color: #e11d48; }
.quick-icon.teal { background: #f0fdfa; color: #0d9488; }
.quick-icon.red { background: #fef2f2; color: #dc2626; }

html.dark .quick-icon.blue { background: rgba(37, 99, 235, 0.15) !important; color: #3b82f6 !important; }
html.dark .quick-icon.green { background: rgba(22, 163, 74, 0.15) !important; color: #4ade80 !important; }
html.dark .quick-icon.amber { background: rgba(217, 119, 6, 0.15) !important; color: #fbbf24 !important; }
html.dark .quick-icon.purple { background: rgba(124, 58, 237, 0.15) !important; color: #a78bfa !important; }
html.dark .quick-icon.indigo { background: rgba(79, 70, 229, 0.15) !important; color: #818cf8 !important; }
html.dark .quick-icon.cyan { background: rgba(8, 145, 178, 0.15) !important; color: #22d3ee !important; }
html.dark .quick-icon.rose { background: rgba(225, 29, 72, 0.15) !important; color: #fb7185 !important; }
html.dark .quick-icon.teal { background: rgba(13, 148, 136, 0.15) !important; color: #2dd4bf !important; }
html.dark .quick-icon.red { background: rgba(220, 38, 38, 0.15) !important; color: #f87171 !important; }

.quick-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.quick-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-main);
}

.quick-desc {
  font-size: 11px;
  color: var(--text-muted);
  font-weight: 500;
}

.quick-arrow {
  color: var(--text-muted);
  font-size: 12px;
  transition: transform 0.2s ease;
}

.quick-card:hover .quick-arrow {
  transform: translateX(3px);
  color: var(--primary-color);
}

/* 底部双栏布局 */
.dashboard-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(320px, 0.9fr);
  gap: 20px;
}

.main-column,
.side-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.panel {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.01);
}

.main-column .panel,
.side-column .panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.compact-panel {
  padding: 24px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.panel-header h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: var(--text-main);
}

.panel-header p {
  margin: 6px 0 0 0;
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 500;
}

.btn-more {
  background: var(--bg-tip-hover);
  border: none;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-tip-hover);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-more:hover {
  background: rgba(37, 99, 235, 0.15);
  color: var(--primary-hover);
}

/* 无边框精美表格 */
.dashboard-table {
  width: 100%;
}

.dashboard-table >>> .el-table__row {
  transition: background-color 0.2s ease;
}

.dashboard-table >>> th.el-table__cell {
  background-color: var(--bg-page) !important;
  color: var(--text-muted);
  font-weight: 700;
  font-size: 12px;
  border-bottom: 1px solid var(--border-color);
  padding: 10px 0;
}

.dashboard-table >>> td.el-table__cell {
  padding: 12px 0;
  border-bottom: 1px solid var(--border-light);
}

.dashboard-table >>> .el-table__body tr:hover > td.el-table__cell {
  background-color: var(--bg-hover) !important;
}

.badge-type {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
}

.badge-type.channel {
  background-color: #eff6ff;
  color: #2563eb;
}

.badge-type.platform {
  background-color: #f0fdf4;
  color: #16a34a;
}

.badge-type.args {
  background-color: #fffbeb;
  color: #d97706;
}

html.dark .badge-type.channel {
  background-color: rgba(37, 99, 235, 0.15) !important;
  color: #60a5fa !important;
}

html.dark .badge-type.platform {
  background-color: rgba(22, 163, 74, 0.15) !important;
  color: #4ade80 !important;
}

html.dark .badge-type.args {
  background-color: rgba(217, 119, 6, 0.15) !important;
  color: #fbbf24 !important;
}

.text-meta {
  font-size: 12px;
  color: var(--text-muted);
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
}

.btn-table-action {
  color: var(--primary-color) !important;
  font-weight: 700 !important;
}

.btn-table-action:hover {
  color: var(--primary-hover) !important;
}

/* 运维工具面板与业务规范手册 CSS */
/* 游戏中台业务接入工作流与配置生效模型 */
.workflow-panel {
  flex: 1;
}

.workflow-steps {
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
  padding-left: 12px;
}

/* 绘制步骤时序线 */
.workflow-steps::before {
  content: "";
  position: absolute;
  left: 21px;
  top: 14px;
  bottom: 14px;
  width: 2px;
  background: var(--border-color);
  z-index: 1;
}

.workflow-step-item {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  position: relative;
  z-index: 2;
}

.step-num {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--primary-color);
  color: #ffffff;
  font-weight: 700;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 4px var(--bg-card);
  flex-shrink: 0;
}

.step-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--primary-color);
  cursor: pointer;
  transition: color 0.15s ease;
  display: inline-block;
  align-self: flex-start;
}

.step-title:hover {
  color: var(--primary-hover);
  text-decoration: underline;
}

.step-desc {
  margin: 0;
  font-size: 12px;
  color: var(--text-muted);
  line-height: 1.4;
}

@media (max-width: 1200px) {
  .quick-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .dashboard-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard-hero {
    flex-direction: column;
    padding: 24px;
  }

  .hero-right {
    width: 100%;
    align-items: flex-start;
    margin-top: 16px;
    height: auto;
  }

  .quick-grid {
    grid-template-columns: 1fr;
  }
}
</style>
